import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

export type ToastKind = "system" | "achievement" | "egg" | "mode";
export interface ToastItem {
    id: number;
    kind: ToastKind;
    title: string;
    body?: string;
}
interface ToastApi {
    toasts: ToastItem[];
    push: (kind: ToastKind, title: string, body?: string) => void;
    dismiss: (id: number) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const idRef = useRef(0);

    const push = useCallback((kind: ToastKind, title: string, body?: string) => {
        setToasts((prev) => {
            if (prev.some((t) => t.title === title)) return prev;
            const item: ToastItem = { id: ++idRef.current, kind, title, body };
            return [...prev, item].slice(-3);
        });
    }, []);

    const dismiss = useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const api = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useToast(): ToastApi {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useToast must be used inside ToastProvider");
    return ctx;
}
